/**
 * Answers the reporter does not type (ADR-0028):
 *  - customer / site / branch lookups are filled from the survey's site
 *    (stored as codes, ADR-0011: storage uses codes, never labels);
 *  - calculated fields are computed from their formula.
 * The device applies this when a draft is created and on every change; the
 * server applies it again on every sync, so the stored values always match
 * the site and the formulas whatever the device sent.
 */
import { computeCalculated } from './formula.js';
import type { Answers, FieldItem, Template } from './schema.js';

/** Codes of the survey's own customer, site and branch. */
export interface LookupValues {
  customer?: string | null;
  site?: string | null;
  branch?: string | null;
}

export const LOOKUP_FIELD_TYPES = ['customer', 'site', 'branch'] as const;
export type LookupFieldType = (typeof LOOKUP_FIELD_TYPES)[number];

export function isLookupField(field: Pick<FieldItem, 'type'>): field is FieldItem & { type: LookupFieldType } {
  return (LOOKUP_FIELD_TYPES as readonly string[]).includes(field.type);
}

/** Fill every customer/site/branch field with the survey's own code. Pure. */
export function applyLookups(template: Template, answers: Answers, values: LookupValues): Answers {
  let fields: Record<string, unknown> | null = null;
  for (const s of template.sections) {
    for (const it of s.items) {
      if (it.kind !== 'field' || !isLookupField(it)) continue;
      const v = values[it.type];
      const cur = (fields ?? answers.fields)[it.key];
      if (v === undefined) continue;
      if ((v ?? undefined) === cur) continue;
      fields ??= { ...answers.fields };
      if (v === null || v === '') delete fields[it.key];
      else fields[it.key] = v;
    }
  }
  return fields ? { ...answers, fields } : answers;
}

/** Lookups, then calculated fields (a formula may not read a lookup, but order keeps it deterministic). */
export function deriveAnswers(template: Template, answers: Answers, lookups: LookupValues): Answers {
  return computeCalculated(template, applyLookups(template, answers, lookups));
}
